import { useState } from 'react';
import { useScore } from '../../../context/ScoreContext';
import {
  scheduleDays,
  buildEventKey,
  buildTaskKey,
  TaskStatus,
} from '../../../data/scheduleData';

const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";

const BADGE_OPTIONS = [
  { label: '任務', color: '#d4a843' },
  { label: '互動', color: '#7b68ee' },
  { label: '競賽', color: '#e05555' },
  { label: '獎勵', color: '#7ec8a0' },
  { label: '儀式', color: '#c06b9a' },
  { label: '機制', color: '#f0a86e' },
];

const BADGE_CLASS_MAP: Record<string, string> = {
  '任務': 'bg-[#d4a843]/20 text-[#d4a843]',
  '互動': 'bg-[#7b68ee]/20 text-[#7b68ee]',
  '競賽': 'bg-[#e05555]/20 text-[#e05555]',
  '獎勵': 'bg-[#7ec8a0]/20 text-[#7ec8a0]',
  '儀式': 'bg-[#c06b9a]/20 text-[#c06b9a]',
  '機制': 'bg-[#f0a86e]/20 text-[#f0a86e]',
};

// ── Add Custom Task Form (standalone day-level) ────────────────────────────
function AddCustomTaskForm({ dayId, onClose }: { dayId: string; onClose: () => void }) {
  const { addCustomTask } = useScore();
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [badge, setBadge] = useState(BADGE_OPTIONS[0].label);
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) { setError('請輸入任務名稱'); return; }
    addCustomTask({ dayId, title: title.trim(), desc: desc.trim(), badge });
    onClose();
  };

  const selectedOption = BADGE_OPTIONS.find(b => b.label === badge) ?? BADGE_OPTIONS[0];

  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0d1020, #080910)', borderColor: '#1e2035' }}>
      <div className="px-4 py-3 border-b border-[#16172a] flex items-center gap-2">
        <div className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0"
          style={{ background: '#e0555515', border: '1px solid #e0555530' }}>
          <i className="ri-add-line text-xs" style={{ color: '#e05555' }} />
        </div>
        <p className="text-white text-xs font-bold flex-1" style={{ fontFamily: FONT_BODY }}>新增臨時任務</p>
        <button onClick={onClose} className="text-[#2a3a5a] hover:text-[#666] cursor-pointer">
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div>
          <p className="text-[#2a3a5a] text-[10px] tracking-[0.3em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>任務名稱 *</p>
          <div className="flex items-center gap-2 rounded-xl px-3 py-2"
            style={{ background: '#0a0b16', border: `1px solid ${error ? '#e05555' : '#16172a'}` }}>
            <i className="ri-clipboard-line text-[#2a3a5a] text-sm shrink-0" />
            <input type="text" value={title}
              onChange={e => { setTitle(e.target.value); setError(''); }}
              placeholder="例：宵夜挑戰賽、寫封信給明日的你"
              maxLength={40}
              className="bg-transparent text-white text-xs w-full outline-none placeholder:text-[#1e2035]"
              style={{ fontFamily: FONT_BODY }} />
          </div>
          {error && <p className="text-[#e05555] text-[10px] mt-1" style={{ fontFamily: FONT_BODY }}>{error}</p>}
        </div>
        <div>
          <p className="text-[#2a3a5a] text-[10px] tracking-[0.3em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>任務類型</p>
          <div className="flex gap-1.5 flex-wrap">
            {BADGE_OPTIONS.map(opt => (
              <button key={opt.label} type="button" onClick={() => setBadge(opt.label)}
                className="px-2.5 py-1 rounded-full text-[10px] font-bold cursor-pointer transition-all whitespace-nowrap"
                style={badge === opt.label
                  ? { background: `${opt.color}20`, color: opt.color, border: `1px solid ${opt.color}40` }
                  : { background: '#0a0b16', color: '#2a3a5a', border: '1px solid #16172a' }
                }>{opt.label}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[#2a3a5a] text-[10px] tracking-[0.3em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>任務說明（可選）</p>
          <textarea value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="任務規則、背景說明、評分方式…" rows={3} maxLength={200}
            className="w-full rounded-xl px-3 py-2 text-white text-xs outline-none resize-none placeholder:text-[#1e2035]"
            style={{ background: '#0a0b16', border: '1px solid #16172a', fontFamily: FONT_BODY }} />
          <p className="text-[#1e2035] text-[10px] text-right mt-0.5" style={{ fontFamily: FONT_BODY }}>{desc.length}/200</p>
        </div>
        <div className="rounded-xl px-3 py-2.5 border flex items-center gap-2"
          style={{ background: `${selectedOption.color}06`, borderColor: `${selectedOption.color}20` }}>
          <span className="text-[10px] px-2 py-0.5 rounded font-bold shrink-0"
            style={{ background: `${selectedOption.color}20`, color: selectedOption.color }}>{badge}</span>
          <span className="text-xs font-medium text-white flex-1 min-w-0 truncate" style={{ fontFamily: FONT_BODY }}>
            {title || '任務名稱預覽'}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0 font-bold"
            style={{ background: 'rgba(126,200,160,0.12)', color: '#7ec8a0', border: '1px solid rgba(126,200,160,0.25)' }}>
            即時解鎖
          </span>
        </div>
        <button type="button" onClick={handleSubmit}
          className="w-full py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-1.5"
          style={{ background: '#e0555518', color: '#e05555', border: '1px solid #e0555530', fontFamily: FONT_BODY }}>
          <i className="ri-send-plane-fill text-xs" />新增並即時同步
        </button>
      </div>
    </div>
  );
}

// ── Add Task Inline Form (within event card) ──────────────────────────────────
function AddTaskInlineForm({
  onSave, onCancel,
}: {
  onSave: (task: { badge: string; badgeColor: string; name: string; desc: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [badge, setBadge] = useState(BADGE_OPTIONS[0].label);
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!name.trim()) { setError('請輸入任務名稱'); return; }
    onSave({
      badge,
      badgeColor: BADGE_CLASS_MAP[badge] ?? BADGE_CLASS_MAP['任務'],
      name: name.trim(),
      desc: desc.trim(),
    });
  };

  const selectedOpt = BADGE_OPTIONS.find(b => b.label === badge) ?? BADGE_OPTIONS[0];

  return (
    <div className="mt-2 mx-0 px-3 py-3 rounded-xl border space-y-2.5"
      style={{ background: '#0a0b16', borderColor: '#7ec8a025' }}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <i className="ri-add-circle-line text-xs" style={{ color: '#7ec8a0' }} />
        <p className="text-[#7ec8a0] text-[9px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: FONT_DECO }}>
          新增任務至此活動
        </p>
      </div>

      {/* Name */}
      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.25em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>任務名稱 *</p>
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
          style={{ background: '#060710', border: `1px solid ${error ? '#e05555' : '#16172a'}` }}>
          <i className="ri-clipboard-line text-[#2a3a5a] text-xs shrink-0" />
          <input
            type="text"
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            maxLength={50}
            placeholder="輸入任務名稱"
            className="bg-transparent text-white text-xs w-full outline-none placeholder:text-[#1a2035]"
            style={{ fontFamily: FONT_BODY }}
            autoFocus
          />
        </div>
        {error && <p className="text-[#e05555] text-[9px] mt-0.5" style={{ fontFamily: FONT_BODY }}>{error}</p>}
      </div>

      {/* Badge */}
      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.25em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>任務類型</p>
        <div className="flex gap-1 flex-wrap">
          {BADGE_OPTIONS.map(opt => (
            <button key={opt.label} type="button" onClick={() => setBadge(opt.label)}
              className="px-2 py-0.5 rounded-full text-[9px] font-bold cursor-pointer transition-all whitespace-nowrap"
              style={badge === opt.label
                ? { background: `${opt.color}20`, color: opt.color, border: `1px solid ${opt.color}40` }
                : { background: '#0a0b16', color: '#2a3a5a', border: '1px solid #16172a' }
              }>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Desc */}
      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.25em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>任務說明（可選）</p>
        <textarea
          value={desc}
          onChange={e => setDesc(e.target.value)}
          rows={2}
          maxLength={300}
          placeholder="任務規則、背景說明…"
          className="w-full rounded-lg px-2.5 py-1.5 text-white text-xs outline-none resize-none placeholder:text-[#1a2035]"
          style={{ background: '#060710', border: '1px solid #16172a', fontFamily: FONT_BODY }}
        />
      </div>

      {/* Preview */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
        style={{ background: `${selectedOpt.color}06`, border: `1px solid ${selectedOpt.color}18` }}>
        <span className="text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0"
          style={{ background: `${selectedOpt.color}20`, color: selectedOpt.color }}>{badge}</span>
        <span className="text-white text-xs truncate flex-1 min-w-0" style={{ fontFamily: FONT_BODY }}>
          {name || '任務名稱預覽'}
        </span>
        <span className="text-[9px] shrink-0" style={{ color: '#7ec8a060' }}>新增</span>
      </div>

      {/* Buttons */}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          className="flex-1 py-1.5 rounded-lg text-[10px] cursor-pointer border whitespace-nowrap"
          style={{ color: '#444', borderColor: '#1e2035', fontFamily: FONT_BODY }}>
          取消
        </button>
        <button type="button" onClick={handleSave}
          className="flex-1 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
          style={{ background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a030', fontFamily: FONT_BODY }}>
          <i className="ri-check-line text-xs" />新增並同步
        </button>
      </div>
    </div>
  );
}

// ── Inline task edit form ─────────────────────────────────────────────────────
function TaskInlineEdit({
  initName, initDesc,
  onSave, onCancel,
}: {
  initName: string; initDesc: string;
  onSave: (name: string, desc: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initName);
  const [desc, setDesc] = useState(initDesc);
  const [error, setError] = useState('');

  return (
    <div className="mt-1.5 mb-1 px-3 py-3 rounded-xl border space-y-2.5"
      style={{ background: '#0a0b16', borderColor: '#1e2035' }}>

      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.3em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>任務名稱 *</p>
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
          style={{ background: '#060710', border: `1px solid ${error ? '#e05555' : '#16172a'}` }}>
          <i className="ri-pencil-line text-[#2a3a5a] text-xs shrink-0" />
          <input
            type="text"
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            maxLength={50}
            className="bg-transparent text-white text-xs w-full outline-none"
            style={{ fontFamily: FONT_BODY }}
            autoFocus
          />
        </div>
        {error && <p className="text-[#e05555] text-[9px] mt-0.5" style={{ fontFamily: FONT_BODY }}>{error}</p>}
      </div>

      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.3em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>任務說明</p>
        <textarea
          value={desc}
          onChange={e => setDesc(e.target.value)}
          rows={3}
          maxLength={300}
          placeholder="任務規則、背景說明、評分方式…"
          className="w-full rounded-lg px-2.5 py-1.5 text-white text-xs outline-none resize-none placeholder:text-[#1a2035]"
          style={{ background: '#060710', border: '1px solid #16172a', fontFamily: FONT_BODY }}
        />
        <p className="text-[#1e2035] text-[9px] text-right" style={{ fontFamily: FONT_BODY }}>{desc.length}/300</p>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          className="flex-1 py-1.5 rounded-lg text-[10px] cursor-pointer border whitespace-nowrap"
          style={{ color: '#444', borderColor: '#1e2035', fontFamily: FONT_BODY }}>
          取消
        </button>
        <button type="button"
          onClick={() => {
            if (!name.trim()) { setError('名稱不可為空'); return; }
            onSave(name.trim(), desc.trim());
          }}
          className="flex-1 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
          style={{ background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a030', fontFamily: FONT_BODY }}>
          <i className="ri-check-line text-xs" />儲存並同步
        </button>
      </div>
    </div>
  );
}

// ── Inline event edit form (title + time) ─────────────────────────────────────
function EventInlineEdit({
  initTitle, initTime,
  onSave, onCancel,
}: {
  initTitle: string; initTime: string;
  onSave: (title: string, time: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initTitle);
  const [time, setTime]   = useState(initTime);
  const [error, setError] = useState('');

  return (
    <div className="mx-4 mb-3 px-3 py-3 rounded-xl border space-y-2.5"
      style={{ background: '#0a0b16', borderColor: '#d4a84325' }}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <i className="ri-edit-2-line text-xs" style={{ color: '#d4a843' }} />
        <p className="text-[#d4a843] text-[9px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: FONT_DECO }}>
          編輯活動資訊
        </p>
      </div>

      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.25em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>活動標題 *</p>
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
          style={{ background: '#060710', border: `1px solid ${error ? '#e05555' : '#1e2035'}` }}>
          <i className="ri-file-text-line text-[#2a3a5a] text-xs shrink-0" />
          <input
            type="text"
            value={title}
            onChange={e => { setTitle(e.target.value); setError(''); }}
            maxLength={60}
            className="bg-transparent text-white text-xs w-full outline-none"
            style={{ fontFamily: FONT_BODY }}
            autoFocus
          />
        </div>
        {error && <p className="text-[#e05555] text-[9px] mt-0.5" style={{ fontFamily: FONT_BODY }}>{error}</p>}
      </div>

      <div>
        <p className="text-[#2a3a5a] text-[9px] tracking-[0.25em] uppercase mb-1" style={{ fontFamily: FONT_DECO }}>活動時間</p>
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
          style={{ background: '#060710', border: '1px solid #1e2035' }}>
          <i className="ri-time-line text-[#2a3a5a] text-xs shrink-0" />
          <input
            type="text"
            value={time}
            onChange={e => setTime(e.target.value)}
            maxLength={30}
            placeholder="例：19:00 - 21:30"
            className="bg-transparent text-white text-xs w-full outline-none placeholder:text-[#1a2035]"
            style={{ fontFamily: FONT_BODY }}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2 rounded-lg text-[10px] cursor-pointer border whitespace-nowrap"
          style={{ color: '#444', borderColor: '#1e2035', fontFamily: FONT_BODY }}>
          取消
        </button>
        <button type="button"
          onClick={() => {
            if (!title.trim()) { setError('標題不可為空'); return; }
            onSave(title.trim(), time.trim());
          }}
          className="flex-1 py-2 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
          style={{ background: '#d4a84318', color: '#d4a843', border: '1px solid #d4a84330', fontFamily: FONT_BODY }}>
          <i className="ri-check-line text-xs" />儲存並同步
        </button>
      </div>
    </div>
  );
}

// ── Delete Confirm Inline ─────────────────────────────────────────────────────
function DeleteConfirmInline({
  label,
  onConfirm,
  onCancel,
}: {
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
      style={{ background: '#e0555510', border: '1px solid #e0555520' }}>
      <i className="ri-error-warning-line text-xs shrink-0" style={{ color: '#e05555' }} />
      <span className="text-[10px] flex-1 min-w-0 truncate" style={{ color: '#e0555590', fontFamily: FONT_BODY }}>
        刪除「{label}」？
      </span>
      <button type="button" onClick={onCancel}
        className="px-2 py-0.5 rounded text-[9px] border cursor-pointer whitespace-nowrap"
        style={{ color: '#444', borderColor: '#1e2035', fontFamily: FONT_BODY }}>
        取消
      </button>
      <button type="button" onClick={onConfirm}
        className="px-2 py-0.5 rounded text-[9px] font-bold cursor-pointer whitespace-nowrap"
        style={{ background: '#e0555520', color: '#e05555', border: '1px solid #e0555530', fontFamily: FONT_BODY }}>
        確認刪除
      </button>
    </div>
  );
}

// ── Event Card ────────────────────────────────────────────────────────────────
function EventCard({
  dayId, dayColor, ev, idx, status, unlockedTasks,
  taskOverrides, eventOverride, addedTasks, taskDeletions,
  onStart, onEnd, onToggleTask, onUpdateTask, onUpdateEvent, onAddTask, onDeleteTask,
}: {
  dayId: string;
  dayColor: string;
  ev: typeof scheduleDays[0]['events'][0];
  idx: number;
  status: TaskStatus;
  unlockedTasks: Record<string, boolean>;
  taskOverrides: Record<string, { name: string; desc: string }>;
  eventOverride: { title: string; time: string } | undefined;
  addedTasks: Array<{ id: string; badge: string; badgeColor: string; name: string; desc: string }>;
  taskDeletions: Record<string, true>;
  onStart: () => void;
  onEnd: () => void;
  onToggleTask: (key: string, name: string) => void;
  onUpdateTask: (taskIdx: number, override: { name: string; desc: string }) => void;
  onUpdateEvent: (override: { title: string; time: string }) => void;
  onAddTask: (task: { badge: string; badgeColor: string; name: string; desc: string }) => void;
  onDeleteTask: (key: string) => void;
}) {
  const isPending = status === 'pending';
  const isOngoing = status === 'ongoing';
  const isDone    = status === 'done';

  const [editingTaskIdx, setEditingTaskIdx] = useState<number | null>(null);
  const [editingEvent, setEditingEvent] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  // Apply event overrides
  const displayTitle = eventOverride?.title ?? ev.title;
  const displayTime  = eventOverride?.time  ?? ev.time;
  const isEventOverridden = !!eventOverride;

  const getDisplayName = (ti: number) => taskOverrides[buildTaskKey(dayId, idx, ti)]?.name ?? ev.tasks[ti].name;
  const getDisplayDesc = (ti: number) => taskOverrides[buildTaskKey(dayId, idx, ti)]?.desc ?? ev.tasks[ti].desc;
  const hasOverride    = (ti: number) => !!taskOverrides[buildTaskKey(dayId, idx, ti)];
  const isDeleted      = (key: string) => !!taskDeletions[key];

  // Active tasks = original not deleted + added
  const activeOriginalCount = ev.tasks.filter((_, ti) => !isDeleted(buildTaskKey(dayId, idx, ti))).length;
  const totalActiveCount = activeOriginalCount + addedTasks.length;
  const allUnlocked = ev.tasks.every((_, ti) => !isDeleted(buildTaskKey(dayId, idx, ti)) && !!unlockedTasks[buildTaskKey(dayId, idx, ti)]);

  return (
    <div className="rounded-2xl overflow-hidden border transition-all duration-300"
      style={{
        background: isOngoing
          ? 'linear-gradient(160deg, #0e1208, #080e04)'
          : isDone
          ? 'linear-gradient(160deg, #080e08, #050a05)'
          : 'linear-gradient(160deg, #0c0d1a, #080910)',
        borderColor: isOngoing ? '#7ec8a040' : isDone ? '#7ec8a025' : '#16172a',
        boxShadow: isOngoing ? '0 0 20px rgba(126,200,160,0.08)' : 'none',
      }}>

      {/* ── Header ── */}
      <div className="px-4 py-3 border-b flex items-start gap-2.5 relative group/header"
        style={{ borderColor: isOngoing ? '#7ec8a018' : isDone ? '#7ec8a010' : '#16172a' }}>
        {isOngoing && (
          <div className="absolute top-0 left-0 right-0 h-px overflow-hidden pointer-events-none">
            <div className="h-full" style={{ background: 'linear-gradient(90deg,transparent,#7ec8a070,transparent)', animation: 'tmScan 2.5s linear infinite' }} />
          </div>
        )}
        <div className="w-8 h-8 flex items-center justify-center rounded-xl shrink-0 mt-0.5 relative"
          style={{
            background: isDone ? '#7ec8a015' : isOngoing ? '#7ec8a018' : `${dayColor}15`,
            border: `1px solid ${isDone ? '#7ec8a025' : isOngoing ? '#7ec8a030' : dayColor + '25'}`,
          }}>
          <i className={`${isDone ? 'ri-check-line' : ev.icon} text-sm`}
            style={{ color: isDone ? '#7ec8a0' : isOngoing ? '#7ec8a0' : dayColor }} />
          {isOngoing && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#7ec8a0]"
              style={{ boxShadow: '0 0 6px #7ec8a0', animation: 'tmPulse 1.5s ease-in-out infinite' }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1.5 mb-1.5">
            <p className="text-white text-xs font-bold leading-snug flex-1 min-w-0" style={{ fontFamily: FONT_BODY }}>
              {displayTitle}
            </p>
            {/* Edit event button */}
            {!editingEvent && (
              <button type="button"
                onClick={() => setEditingEvent(true)}
                className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 cursor-pointer transition-all opacity-0 group-hover/header:opacity-100"
                style={{ color: '#2a3a5a' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#d4a843'; (e.currentTarget as HTMLElement).style.background = '#d4a84312'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#2a3a5a'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                title="編輯活動名稱與時間">
                <i className="ri-edit-2-line text-xs" />
              </button>
            )}
            {editingEvent && (
              <button type="button" onClick={() => setEditingEvent(false)}
                className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 cursor-pointer"
                style={{ color: '#e05555', background: '#e0555510' }}>
                <i className="ri-close-line text-xs" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ background: `${dayColor}15`, color: dayColor, border: `1px solid ${dayColor}25`, fontFamily: FONT_BODY }}>
              {displayTime}
            </span>
            {isPending && (
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold"
                style={{ background: '#16172a', color: '#3a4a6a', border: '1px solid #1e2035', fontFamily: FONT_BODY }}>
                未開始
              </span>
            )}
            {isOngoing && (
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1"
                style={{ background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a030', fontFamily: FONT_BODY }}>
                <i className="ri-loader-4-line animate-spin text-[9px]" />進行中
              </span>
            )}
            {isDone && (
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1"
                style={{ background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a025', fontFamily: FONT_BODY }}>
                <i className="ri-checkbox-circle-fill text-[9px]" />已完成
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Event inline edit form ── */}
      {editingEvent && (
        <EventInlineEdit
          initTitle={displayTitle}
          initTime={displayTime}
          onSave={(title, time) => {
            onUpdateEvent({ title, time });
            setEditingEvent(false);
          }}
          onCancel={() => setEditingEvent(false)}
        />
      )}

      {/* ── Task list (all states) ── */}
      <div className="px-4 pt-3 pb-0">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[#2a3a5a] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: FONT_DECO }}>
            任務清單
          </p>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full"
            style={{ background: '#0e0f1e', color: '#2a3a5a', border: '1px solid #16172a', fontFamily: FONT_BODY }}>
            {totalActiveCount} 個任務
          </span>
        </div>
        <div className="space-y-1.5 pb-1">
          {/* ── Original tasks ── */}
          {ev.tasks.map((task, ti) => {
            const tKey       = buildTaskKey(dayId, idx, ti);
            if (isDeleted(tKey)) return null;
            const isUnlocked = !!unlockedTasks[tKey];
            const isEditing  = editingTaskIdx === ti;
            const dispName   = getDisplayName(ti);
            const dispDesc   = getDisplayDesc(ti);
            const overridden = hasOverride(ti);
            const isConfirmingDelete = deletingKey === tKey;

            return (
              <div key={ti}>
                {/* Delete confirm row */}
                {isConfirmingDelete && (
                  <DeleteConfirmInline
                    label={dispName}
                    onConfirm={() => { onDeleteTask(tKey); setDeletingKey(null); }}
                    onCancel={() => setDeletingKey(null)}
                  />
                )}
                {/* Task row */}
                {!isConfirmingDelete && (
                  <div className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg group"
                    style={{
                      background: isOngoing ? (isUnlocked ? 'rgba(126,200,160,0.05)' : '#08090f') : '#08090f',
                      border: `1px solid ${isOngoing && isUnlocked ? '#7ec8a015' : '#12131e'}`,
                    }}>

                    {isOngoing && (
                      <button type="button" onClick={() => onToggleTask(tKey, dispName)}
                        className="w-5 h-5 flex items-center justify-center shrink-0 cursor-pointer transition-all"
                        title={isUnlocked ? '點擊鎖定' : '點擊解鎖'}>
                        <i className={`${isUnlocked ? 'ri-lock-unlock-line' : 'ri-lock-line'} text-xs`}
                          style={{ color: isUnlocked ? '#7ec8a0' : '#2a3a5a' }} />
                      </button>
                    )}
                    {!isOngoing && (
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <div className="w-1.5 h-1.5 rounded-full"
                          style={{ background: isDone ? '#7ec8a060' : '#2a3a5a' }} />
                      </div>
                    )}

                    <span className="text-xs flex-1 min-w-0 truncate font-medium"
                      style={{ color: isDone ? '#4a7a5a' : isOngoing && isUnlocked ? '#a0d8b8' : '#4a5a7a', fontFamily: FONT_BODY }}>
                      {dispName}
                    </span>

                    {overridden && !isEditing && (
                      <span className="text-[8px] px-1 py-0.5 rounded shrink-0"
                        style={{ background: '#d4a84312', color: '#d4a84380', fontFamily: FONT_BODY }}>
                        已編輯
                      </span>
                    )}

                    {isOngoing && (
                      <span className="text-[9px] px-1.5 py-px rounded-full shrink-0 whitespace-nowrap"
                        style={isUnlocked
                          ? { background: 'rgba(126,200,160,0.12)', color: '#7ec8a0', border: '1px solid rgba(126,200,160,0.2)', fontFamily: FONT_BODY }
                          : { background: '#0e0f1e', color: '#2a3a5a', border: '1px solid #16172a', fontFamily: FONT_BODY }
                        }>
                        {isUnlocked ? '已解鎖' : '鎖定'}
                      </span>
                    )}

                    {/* Edit button */}
                    {!isEditing && !isConfirmingDelete && (
                      <button type="button"
                        onClick={() => setEditingTaskIdx(ti)}
                        className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                        style={{ color: '#2a3a5a' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#d4a843'; (e.currentTarget as HTMLElement).style.background = '#d4a84312'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#2a3a5a'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        title="編輯此任務">
                        <i className="ri-pencil-line text-xs" />
                      </button>
                    )}

                    {/* Delete button */}
                    {!isEditing && !isConfirmingDelete && (
                      <button type="button"
                        onClick={() => setDeletingKey(tKey)}
                        className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                        style={{ color: '#2a3a5a' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e05555'; (e.currentTarget as HTMLElement).style.background = '#e0555512'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#2a3a5a'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        title="刪除此任務">
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    )}

                    {isEditing && (
                      <button type="button" onClick={() => setEditingTaskIdx(null)}
                        className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 cursor-pointer"
                        style={{ color: '#e05555', background: '#e0555510' }}>
                        <i className="ri-close-line text-xs" />
                      </button>
                    )}
                  </div>
                )}

                {/* Inline edit form */}
                {isEditing && (
                  <TaskInlineEdit
                    initName={dispName}
                    initDesc={dispDesc}
                    onSave={(name, desc) => {
                      onUpdateTask(ti, { name, desc });
                      setEditingTaskIdx(null);
                    }}
                    onCancel={() => setEditingTaskIdx(null)}
                  />
                )}
              </div>
            );
          })}

          {/* ── Added tasks ── */}
          {addedTasks.map((task) => {
            const badgeOpt = BADGE_OPTIONS.find(b => b.label === task.badge) ?? BADGE_OPTIONS[0];
            const isConfirmingDelete = deletingKey === task.id;

            return (
              <div key={task.id}>
                {isConfirmingDelete && (
                  <DeleteConfirmInline
                    label={task.name}
                    onConfirm={() => { onDeleteTask(task.id); setDeletingKey(null); }}
                    onCancel={() => setDeletingKey(null)}
                  />
                )}
                {!isConfirmingDelete && (
                  <div className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg group"
                    style={{ background: '#06080f', border: '1px solid #7ec8a015' }}>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-add-line text-[10px]" style={{ color: '#7ec8a060' }} />
                    </div>

                    <span className="text-xs flex-1 min-w-0 truncate font-medium"
                      style={{ color: '#7ec8a080', fontFamily: FONT_BODY }}>
                      {task.name}
                    </span>

                    <span className="text-[8px] px-1.5 py-0.5 rounded font-bold shrink-0"
                      style={{ background: `${badgeOpt.color}15`, color: `${badgeOpt.color}80` }}>
                      {task.badge}
                    </span>
                    <span className="text-[8px] px-1 py-0.5 rounded shrink-0"
                      style={{ background: '#7ec8a010', color: '#7ec8a050', fontFamily: FONT_BODY }}>
                      新增
                    </span>

                    {/* Delete button for added task */}
                    <button type="button"
                      onClick={() => setDeletingKey(task.id)}
                      className="w-6 h-6 flex items-center justify-center rounded-md shrink-0 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                      style={{ color: '#2a3a5a' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e05555'; (e.currentTarget as HTMLElement).style.background = '#e0555512'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#2a3a5a'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      title="移除此任務">
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* ── Add task inline form ── */}
          {showAddTask && (
            <AddTaskInlineForm
              onSave={(task) => {
                onAddTask(task);
                setShowAddTask(false);
              }}
              onCancel={() => setShowAddTask(false)}
            />
          )}

          {/* ── Add task button ── */}
          {!showAddTask && (
            <button type="button" onClick={() => setShowAddTask(true)}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-all mt-1 whitespace-nowrap"
              style={{ background: '#7ec8a006', color: '#7ec8a050', border: '1px dashed #7ec8a020', fontFamily: FONT_BODY }}>
              <i className="ri-add-line text-xs" />
              新增任務
            </button>
          )}
        </div>
      </div>

      {/* ── Action area ── */}
      <div className="px-4 pb-3 border-t" style={{ borderColor: isOngoing ? '#7ec8a012' : isDone ? '#7ec8a008' : '#12131e' }}>
        <div className="pt-3">
          {isPending && (
            <button type="button" onClick={onStart}
              className="w-full flex items-center justify-center gap-2.5 py-3 rounded-xl font-bold text-sm cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #7ec8a025, #7ec8a010)',
                border: '1px solid #7ec8a040',
                color: '#7ec8a0',
                fontFamily: FONT_BODY,
              }}>
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #7ec8a030, #7ec8a015)' }} />
              <div className="relative flex items-center gap-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-full"
                  style={{ background: '#7ec8a025', border: '1px solid #7ec8a050' }}>
                  <i className="ri-play-fill text-sm" style={{ color: '#7ec8a0' }} />
                </div>
                <div className="text-left">
                  <div className="font-black tracking-wide">開始活動</div>
                  <div className="text-[10px] font-normal opacity-60 mt-0.5">
                    自動解鎖 {ev.tasks.length} 個任務 · 即時同步玩家畫面
                  </div>
                </div>
              </div>
            </button>
          )}

          {isOngoing && (
            <div className="space-y-2">
              {allUnlocked && (
                <div className="flex items-center justify-center">
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: '#7ec8a015', color: '#7ec8a060', border: '1px solid #7ec8a020', fontFamily: FONT_BODY }}>
                    全數已解鎖
                  </span>
                </div>
              )}
              <button type="button" onClick={onEnd}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap"
                style={{
                  background: 'linear-gradient(135deg, #e0555520, #e0555510)',
                  border: '1px solid #e0555540',
                  color: '#e05555',
                  fontFamily: FONT_BODY,
                }}>
                <i className="ri-stop-circle-line text-base" />
                <span className="font-black tracking-wide">結束活動</span>
                <span className="text-[10px] font-normal opacity-60">→ 玩家解鎖領獎按鈕</span>
              </button>
            </div>
          )}

          {isDone && (
            <div className="flex items-center gap-2.5 py-2 px-3 rounded-xl"
              style={{ background: 'rgba(126,200,160,0.04)', border: '1px solid rgba(126,200,160,0.12)' }}>
              <div className="w-7 h-7 flex items-center justify-center rounded-full shrink-0"
                style={{ background: '#7ec8a015', border: '1px solid #7ec8a030' }}>
                <i className="ri-trophy-line text-xs" style={{ color: '#7ec8a0' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#7ec8a0] text-xs font-bold" style={{ fontFamily: FONT_BODY }}>活動已完成</p>
                <p className="text-[#2a4a3a] text-[10px]" style={{ fontFamily: FONT_BODY }}>
                  玩家可點擊「領取獎勵」獲得默契分
                </p>
              </div>
              <button type="button" onClick={onEnd}
                className="text-[#2a3a5a] hover:text-[#e05555] cursor-pointer transition-colors shrink-0"
                title="取消完成"
                style={{ fontFamily: FONT_BODY }}>
                <i className="ri-restart-line text-sm" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function TaskManagementPanel() {
  const {
    scheduleState, setEventStatus, toggleTaskUnlock,
    removeCustomTask, startEventAndUnlockTasks,
    setCustomTaskStatus, updateTaskOverride, updateEventOverride,
    addTaskToEvent, deleteTask,
  } = useScore();
  const { eventStatuses, unlockedTasks, customTasks, taskOverrides, eventOverrides, taskAdditions, taskDeletions } = scheduleState;
  const [activeDay, setActiveDay] = useState('day0');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const getStatus = (dayId: string, idx: number): TaskStatus =>
    (eventStatuses[buildEventKey(dayId, idx)] as TaskStatus) ?? 'pending';

  const handleStart = (dayId: string, eventIdx: number, title: string) => {
    startEventAndUnlockTasks(dayId, eventIdx);
    setFeedback(`▶ 已開始「${title}」`);
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleEnd = (dayId: string, eventIdx: number, title: string) => {
    setEventStatus(buildEventKey(dayId, eventIdx), 'done');
    setFeedback(`✓ 已結束「${title}」— 玩家可領獎`);
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleToggleTask = (key: string, name: string) => {
    toggleTaskUnlock(key);
    const wasLocked = !unlockedTasks[key];
    setFeedback(wasLocked ? `✓ 已解鎖「${name}」` : `已鎖定「${name}」`);
    setTimeout(() => setFeedback(null), 1500);
  };

  const handleUpdateTask = (dayId: string, eventIdx: number, taskIdx: number, override: { name: string; desc: string }) => {
    const key = buildTaskKey(dayId, eventIdx, taskIdx);
    updateTaskOverride(key, override);
    setFeedback(`✓ 已更新任務「${override.name}」— 玩家畫面已同步`);
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleUpdateEvent = (dayId: string, eventIdx: number, override: { title: string; time: string }) => {
    const key = buildEventKey(dayId, eventIdx);
    updateEventOverride(key, override);
    setFeedback(`✓ 已更新活動「${override.title}」— 玩家畫面已同步`);
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleAddTask = (dayId: string, eventIdx: number, task: { badge: string; badgeColor: string; name: string; desc: string }) => {
    addTaskToEvent(dayId, eventIdx, task);
    setFeedback(`✓ 已新增任務「${task.name}」— 玩家畫面已同步`);
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleDeleteTask = (key: string, name?: string) => {
    deleteTask(key);
    setFeedback(`✓ 已刪除任務${name ? `「${name}」` : ''}— 玩家畫面已同步`);
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleDeleteCustomTask = (id: string) => {
    removeCustomTask(id);
    setConfirmDeleteId(null);
    setFeedback('✓ 臨時任務已刪除');
    setTimeout(() => setFeedback(null), 1500);
  };

  const currentDay = scheduleDays.find(d => d.id === activeDay)!;
  const currentCustomTasks = customTasks.filter(t => t.dayId === activeDay);

  const countStatus = (dayId: string) => {
    const day = scheduleDays.find(d => d.id === dayId)!;
    const ongoing = day.events.filter((_, i) => (eventStatuses[buildEventKey(dayId, i)] as TaskStatus) === 'ongoing').length;
    const done    = day.events.filter((_, i) => (eventStatuses[buildEventKey(dayId, i)] as TaskStatus) === 'done').length;
    const total   = day.events.length;
    return { ongoing, done, total };
  };

  return (
    <div className="px-4 py-4 space-y-4">

      {/* Day tabs */}
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-2" style={{ fontFamily: FONT_DECO }}>選擇日期</p>
        <div className="flex gap-1.5 p-1 rounded-xl" style={{ background: '#08090e', border: '1px solid #12131e' }}>
          {scheduleDays.map(d => {
            const { ongoing, done, total } = countStatus(d.id);
            const customCount = customTasks.filter(t => t.dayId === d.id).length;
            const isActive = activeDay === d.id;
            return (
              <button key={d.id} onClick={() => { setActiveDay(d.id); setShowAddForm(false); }}
                className="flex-1 py-2.5 rounded-lg text-xs font-bold cursor-pointer transition-all duration-200 whitespace-nowrap relative"
                style={isActive
                  ? { background: `${d.color}15`, color: d.color, border: `1px solid ${d.color}30` }
                  : { color: '#2a3a5a', border: '1px solid transparent' }
                }>
                {customCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-[8px] font-black flex items-center justify-center"
                    style={{ background: '#e05555', color: '#fff' }}>
                    {customCount}
                  </span>
                )}
                <div style={{ fontFamily: FONT_BODY }}>{d.label}</div>
                <div className="text-[9px] mt-0.5" style={{ color: isActive ? `${d.color}80` : '#1e2035', fontFamily: FONT_BODY }}>
                  {total > 0 ? `${done}/${total} 完成` : '—'}
                  {ongoing > 0 && <span style={{ color: '#7ec8a080' }}> · {ongoing} 進行</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Add custom task form */}
      {showAddForm && (
        <AddCustomTaskForm dayId={activeDay} onClose={() => setShowAddForm(false)} />
      )}

      {/* Custom Tasks List */}
      {currentCustomTasks.length > 0 && (
        <div>
          <p className="text-[#e05555] text-[10px] tracking-[0.35em] uppercase mb-2" style={{ fontFamily: FONT_DECO }}>
            🚨 臨時任務 ({currentCustomTasks.length})
          </p>
          <div className="space-y-2">
            {currentCustomTasks.map(task => {
              const badgeOpt = BADGE_OPTIONS.find(b => b.label === task.badge) ?? BADGE_OPTIONS[0];
              const isConfirming = confirmDeleteId === task.id;
              const taskStatus: TaskStatus = task.status ?? 'ongoing';
              const isOngoing = taskStatus === 'ongoing';
              const isDone = taskStatus === 'done';

              return (
                <div key={task.id} className="rounded-xl border overflow-hidden transition-all duration-300"
                  style={{
                    background: isDone
                      ? 'linear-gradient(160deg, #060d06, #040908)'
                      : 'linear-gradient(160deg, #0c0d1a, #080910)',
                    borderColor: isDone ? '#7ec8a025' : '#e0555520',
                    boxShadow: isOngoing ? '0 0 14px rgba(224,85,85,0.07)' : 'none',
                  }}>
                  <div className="px-3 py-2.5 flex items-start gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0 mt-0.5"
                      style={{
                        background: isDone ? '#7ec8a012' : '#e0555512',
                        border: `1px solid ${isDone ? '#7ec8a025' : '#e0555525'}`,
                      }}>
                      <i className={isDone ? 'ri-check-line text-xs' : 'ri-alarm-warning-line text-xs'}
                        style={{ color: isDone ? '#7ec8a0' : '#e05555' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0"
                          style={{ background: `${badgeOpt.color}20`, color: badgeOpt.color }}>
                          {task.badge}
                        </span>
                        {isOngoing && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1 shrink-0"
                            style={{ background: '#e0555515', color: '#e05555', border: '1px solid #e0555530', fontFamily: FONT_BODY }}>
                            <i className="ri-loader-4-line animate-spin text-[9px]" />進行中
                          </span>
                        )}
                        {isDone && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1 shrink-0"
                            style={{ background: '#7ec8a015', color: '#7ec8a0', border: '1px solid #7ec8a025', fontFamily: FONT_BODY }}>
                            <i className="ri-checkbox-circle-fill text-[9px]" />已完成
                          </span>
                        )}
                        <span className="text-white text-xs font-bold leading-snug flex-1 min-w-0 truncate" style={{ fontFamily: FONT_BODY }}>
                          {task.title}
                        </span>
                      </div>
                      {task.desc && (
                        <p className="text-[#2a3a5a] text-[10px] leading-relaxed" style={{ fontFamily: FONT_BODY }}>
                          {task.desc}
                        </p>
                      )}
                    </div>
                    {!isConfirming ? (
                      <button onClick={() => setConfirmDeleteId(task.id)}
                        className="shrink-0 w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-all text-[#2a3a5a] hover:text-[#e05555] hover:bg-[#e0555510]">
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    ) : (
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => setConfirmDeleteId(null)}
                          className="px-2 py-1 rounded-lg text-[10px] border cursor-pointer text-[#444] border-[#1e2035] hover:text-[#888] whitespace-nowrap">
                          取消
                        </button>
                        <button onClick={() => handleDeleteCustomTask(task.id)}
                          className="px-2 py-1 rounded-lg text-[10px] font-bold cursor-pointer whitespace-nowrap"
                          style={{ background: 'rgba(224,85,85,0.15)', color: '#e05555', border: '1px solid rgba(224,85,85,0.3)' }}>
                          刪除
                        </button>
                      </div>
                    )}
                  </div>

                  {!isConfirming && (
                    <div className="px-3 pb-3 border-t" style={{ borderColor: isDone ? '#7ec8a010' : '#e0555514' }}>
                      <div className="pt-2.5">
                        {isOngoing && (
                          <button type="button"
                            onClick={() => {
                              setCustomTaskStatus(task.id, 'done');
                              setFeedback(`✓ 已結束「${task.title}」— 玩家可領獎`);
                              setTimeout(() => setFeedback(null), 2000);
                            }}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-xs cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap"
                            style={{
                              background: 'linear-gradient(135deg, #e0555520, #e0555510)',
                              border: '1px solid #e0555540',
                              color: '#e05555',
                              fontFamily: FONT_BODY,
                            }}>
                            <i className="ri-stop-circle-line text-sm" />
                            <span className="font-black tracking-wide">結束臨時任務</span>
                            <span className="text-[9px] font-normal opacity-60">→ 玩家解鎖領獎按鈕</span>
                          </button>
                        )}
                        {isDone && (
                          <div className="flex items-center gap-2.5">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                                style={{ background: '#7ec8a015', border: '1px solid #7ec8a030' }}>
                                <i className="ri-trophy-line text-[10px]" style={{ color: '#7ec8a0' }} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[#7ec8a0] text-xs font-bold" style={{ fontFamily: FONT_BODY }}>任務已完成</p>
                                <p className="text-[#2a4a3a] text-[9px]" style={{ fontFamily: FONT_BODY }}>玩家可點擊「領取獎勵」獲得默契分</p>
                              </div>
                            </div>
                            <button type="button"
                              onClick={() => {
                                setCustomTaskStatus(task.id, 'ongoing');
                                setFeedback(`↩ 「${task.title}」已重設為進行中`);
                                setTimeout(() => setFeedback(null), 1500);
                              }}
                              className="text-[#2a3a5a] hover:text-[#e05555] cursor-pointer transition-colors shrink-0"
                              title="重設為進行中"
                              style={{ fontFamily: FONT_BODY }}>
                              <i className="ri-restart-line text-sm" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add button */}
      {!showAddForm && (
        <button type="button" onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
          style={{ background: '#e0555508', color: '#e05555', border: '1px dashed #e0555530', fontFamily: FONT_BODY }}>
          <i className="ri-alarm-warning-line text-sm" />
          為 {currentDay.label} 新增臨時任務
        </button>
      )}

      {/* Divider */}
      <div className="flex items-center gap-2">
        <div className="h-px flex-1" style={{ background: '#16172a' }} />
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: FONT_DECO }}>活動流程</p>
        <div className="h-px flex-1" style={{ background: '#16172a' }} />
      </div>

      {/* Event cards */}
      <div className="space-y-3">
        {currentDay.events.length === 0 ? (
          <div className="py-8 text-center">
            <i className="ri-calendar-line text-2xl text-[#2a3a5a] mb-2 block" />
            <p className="text-[#2a3a5a] text-xs" style={{ fontFamily: FONT_BODY }}>此日暫無活動</p>
          </div>
        ) : (
          currentDay.events.map((ev, idx) => {
            const status = getStatus(currentDay.id, idx);
            const evKey = buildEventKey(currentDay.id, idx);
            const addedForEvent = taskAdditions?.[evKey] ?? [];
            return (
              <EventCard
                key={idx}
                dayId={currentDay.id}
                dayColor={currentDay.color}
                ev={ev}
                idx={idx}
                status={status}
                unlockedTasks={unlockedTasks}
                taskOverrides={taskOverrides ?? {}}
                eventOverride={(eventOverrides ?? {})[evKey]}
                addedTasks={addedForEvent}
                taskDeletions={taskDeletions ?? {}}
                onStart={() => handleStart(currentDay.id, idx, ev.title)}
                onEnd={() => handleEnd(currentDay.id, idx, ev.title)}
                onToggleTask={handleToggleTask}
                onUpdateTask={(taskIdx, override) => handleUpdateTask(currentDay.id, idx, taskIdx, override)}
                onUpdateEvent={(override) => handleUpdateEvent(currentDay.id, idx, override)}
                onAddTask={(task) => handleAddTask(currentDay.id, idx, task)}
                onDeleteTask={(key) => handleDeleteTask(key)}
              />
            );
          })
        )}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="flex items-center justify-center gap-2 py-3 rounded-xl border border-[#7ec8a0]/20"
          style={{ background: 'rgba(126,200,160,0.05)' }}>
          <i className="ri-check-circle-fill text-[#7ec8a0] text-sm" />
          <span className="text-[#7ec8a0] text-sm font-bold" style={{ fontFamily: FONT_BODY }}>{feedback}</span>
        </div>
      )}

      <style>{`
        @keyframes tmScan {
          from { transform: translateX(-100%); }
          to   { transform: translateX(100%); }
        }
        @keyframes tmPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
